import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import Header from "@/components/site/Header";
import Ticker from "@/components/site/Ticker";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";

export default function CountryPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/seo/country/${(code || "").toUpperCase()}`).then((r) => setData(r.data)).catch((e) => setErr(e));
  }, [code]);

  if (err) return (
    <div className="min-h-screen"><Header /><Ticker />
      <div className="mx-auto max-w-[1400px] px-6 py-20 text-center">
        <h1 className="font-display font-extrabold text-5xl tracking-tighter">Country not found</h1>
        <Link to="/" className="overline mt-6 inline-block border border-black px-5 py-2">← Atlas</Link>
      </div><Footer /></div>
  );
  if (!data) return null;

  const total = data.items.reduce((s, t) => s + t.volume, 0);
  const title = `What ${data.name} is searching for | Universtrend`;
  const desc = `Live ranking of the most-searched terms in ${data.name}: ${data.items.slice(0,3).map((t)=>t.term).join(", ")} and ${data.items.length - 3} more — across People, Tech, Places, Food, Sports.`;

  return (
    <div data-testid="country-page" className="bg-white text-[#0a0a0a]">
      <Seo title={title} description={desc} />
      <Header />
      <Ticker />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="overline text-[#52525b] mb-4">Country · {data.code}</div>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl lg:text-8xl">
            What {data.name}<br /><span className="text-[#ff3b30]">is searching for.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-[#52525b]">
            The top {data.items.length} most-searched terms in {data.name} this month, hand-curated and ranked by estimated monthly volume — totalling <b>{fmt(total)}</b> searches.
          </p>
        </div>
      </section>

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 space-y-12">
          {Object.entries(data.by_category).map(([cat, items]) => (
            <div key={cat}>
              <div className="flex items-baseline justify-between mb-4 border-b border-black pb-2">
                <h2 className="font-display font-extrabold text-2xl lg:text-4xl tracking-tighter">{cat}</h2>
                <Link to={`/category/${cat.toLowerCase()}`} className="overline text-[#52525b] hover:text-black">All {cat} →</Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-black">
                {items.map((t) => (
                  <Link key={t.id} to={`/trend/${t.id}`} data-testid={`country-trend-${t.id}`} className="block border-r border-b border-black p-5 hover:bg-[#fafafa]">
                    <div className="flex items-baseline justify-between">
                      <span className="font-display font-extrabold text-xl tracking-tight">{t.term}</span>
                      <span className="font-mono text-xs text-[#52525b]">#{t.rank}</span>
                    </div>
                    <div className="mt-3 flex justify-between font-mono text-xs">
                      <span>{fmt(t.volume)}/mo</span>
                      <span className={t.delta >= 0 ? "text-[#ff3b30]" : "text-[#52525b]"}>
                        {t.delta >= 0 ? "▲" : "▼"} {Math.abs(t.delta)}%
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
